export interface AbandonedCart {
  /**
   * Original cart ID
   * @format GUID
   */
  id?: string;
  /** Cart status */
  status?: StatusWithLiterals;
  /**
   * Time the cart was abandoned
   * @readonly
   */
  abandonTime?: Date | null;
  /** Buyer information */
  buyerInfo?: BuyerInfo;
  /** Cart total including currency symbol */
  total?: string | null;
  /**
   * History activities
   * @readonly
   */
  activities?: Activity[];
}

export enum Status {
  ABANDONED = 'ABANDONED',
  RECOVERED = 'RECOVERED',
}

/** @enumType */
export type StatusWithLiterals = Status | 'ABANDONED' | 'RECOVERED';

export interface BuyerInfo {
  /**
   * Wix customer ID
   * @format GUID
   */
  id?: string;
  /** Customer information */
  identityType?: IdentityWithLiterals;
  /**
   * Customer's email address
   * @maxLength 50
   * @format EMAIL
   */
  email?: string | null;
  /**
   * Customer's phone number
   * @format PHONE
   */
  phone?: string | null;
  /** Customer's first name */
  firstName?: string | null;
  /** Customer's last name */
  lastName?: string | null;
}

export enum Identity {
  /** Customer is the site owner */
  ADMIN = 'ADMIN',
  /** Customer is logged in */
  MEMBER = 'MEMBER',
  /** Customer is not logged in */
  VISITOR = 'VISITOR',
  /** Contact was created for the customer */
  CONTACT = 'CONTACT',
}

/** @enumType */
export type IdentityWithLiterals =
  | Identity
  | 'ADMIN'
  | 'MEMBER'
  | 'VISITOR'
  | 'CONTACT';

export interface Activity {
  /**
   * Log item type
   * @readonly
   */
  activityType?: ActivityTypeWithLiterals;
  /**
   * Comment added to Log item
   * @maxLength 1000
   * @readonly
   */
  message?: string | null;
  /**
   * Log item occurrence timestamp
   * @readonly
   */
  timestamp?: Date | null;
  /** Custom data for un-typed activities */
  customData?: CustomData;
}

export enum ActivityType {
  UNRECOGNIZED_TYPE = 'UNRECOGNIZED_TYPE',
  SCHEDULED = 'SCHEDULED',
  EMAIL_SENT = 'EMAIL_SENT',
  EMAIL_NOT_SENT = 'EMAIL_NOT_SENT',
  NOTIFICATION_SENT = 'NOTIFICATION_SENT',
  TASK_CREATED = 'TASK_CREATED',
  CUSTOM_ACTIVITY = 'CUSTOM_ACTIVITY',
}

/** @enumType */
export type ActivityTypeWithLiterals =
  | ActivityType
  | 'UNRECOGNIZED_TYPE'
  | 'SCHEDULED'
  | 'EMAIL_SENT'
  | 'EMAIL_NOT_SENT'
  | 'NOTIFICATION_SENT'
  | 'TASK_CREATED'
  | 'CUSTOM_ACTIVITY';

export interface CustomData {
  /**
   * Activity unique namespace
   * @maxLength 50
   */
  namespace?: string | null;
  /** Custom json field for any desired data */
  customValue?: Record<string, any> | null;
}

export interface CartAbandonedEvent {
  /** @format GUID */
  cartId?: string;
  /**
   * Time the cart was created
   * @readonly
   */
  creationTime?: Date | null;
  /**
   * Time the cart was abandoned
   * @readonly
   */
  abandonTime?: Date | null;
  /** Buyer information */
  buyerInfo?: BuyerInfo;
  /** Amount of items in cart */
  itemsCount?: number;
  /** Coupon ID (if relevant) */
  couponId?: string;
  /** Subtotal of all line items in cart, not before shipping and taxes */
  totals?: Totals;
  /**
   * Checkout URL - checkout with the abandoned cart details
   * @format WEB_URL
   */
  checkoutUrl?: string;
}

export interface Totals {
  /**
   * Subtotal of all line items in cart, without shipping and taxes
   * @max 999999999.99
   */
  subtotal?: number | null;
  /**
   * Total cart price
   * @max 999999999.99
   */
  total?: number | null;
  /** Formatted total cart price includes currency symbol */
  formattedTotal?: string;
}

export interface CartRecoveredEvent {
  /** @format GUID */
  cartId?: string;
  /**
   * Time the cart was recovered
   * @readonly
   */
  recoveredTime?: Date | null;
  /**
   * Time the cart was created
   * @readonly
   */
  creationTime?: Date | null;
  /**
   * Time the cart was abandoned
   * @readonly
   */
  abandonedTime?: Date | null;
}

export interface CartDeletedEvent {
  /** @format GUID */
  instanceId?: string;
  /** @format GUID */
  cartId?: string;
}

export interface GetAbandonedCartRequest {
  /**
   * Cart ID
   * @format GUID
   */
  id: string;
}

export interface GetAbandonedCartResponse {
  /** Cart details */
  abandonedCart?: AbandonedCart;
}

export interface QueryAbandonedCartsRequest {
  /** Query details */
  query?: Query;
}

export interface Query {
  paging?: Paging;
  /** A filter string, for more information see the intro section */
  filter?: string | null;
  /** Sort string, for more information see the intro section */
  sort?: string | null;
}

export interface Paging {
  /**
   * The number of items to load
   * @max 100
   */
  limit?: number | null;
  /** The offset since the beginning of the collection */
  offset?: number | null;
}

export interface QueryAbandonedCartsResponse {
  /** Abandoned carts list */
  abandonedCarts?: AbandonedCart[];
  /** Total number of carts */
  totalResults?: number;
}

export interface DeleteCartRequest {
  /**
   * Cart ID
   * @format GUID
   */
  id?: string;
}

export interface DeleteCartResponse {}

export interface RedirectToCheckoutRequest {
  /**
   * abandoned cart id
   * @format GUID
   */
  id?: string;
  /**
   * Identifier of the metaSite this cart uses
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * The currency code to create the redirection link with
   * @format CURRENCY
   */
  currency?: string | null;
}

export interface RawHttpResponse {
  body?: Uint8Array;
  statusCode?: number | null;
  headers?: HeadersEntry[];
}

export interface HeadersEntry {
  key?: string;
  value?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}
